import { useEffect, useState, useRef } from "react";
import api, { API_BASE } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Camera, Image as ImageIcon, Trash2, Upload } from "lucide-react";

function AuthedImage({ photoId, alt, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let objectUrl = null;
    const token = localStorage.getItem("popcrn_token");
    fetch(`${API_BASE}/photos/file/${photoId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r)))
      .then((b) => { objectUrl = URL.createObjectURL(b); setSrc(objectUrl); })
      .catch(() => setSrc(null));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [photoId]);
  if (!src) return <div className={`${className} bg-neutral-200 animate-pulse`} />;
  return <img src={src} alt={alt} className={className} />;
}

/**
 * PhotoAttachments - upload + gallery for a receiving batch or an outbound
 * delivery note. Type "receive" for Wareneingang, "outbound" for Warenausgang.
 */
export default function PhotoAttachments({ refType, refId, label, compact = false, maxPhotos = null }) {
  const [photos, setPhotos] = useState([]);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("popcrn_token") : null;
  const atMax = maxPhotos != null && photos.length >= maxPhotos;

  const load = async () => {
    if (!refId) return;
    try {
      const { data } = await api.get(`/photos/${refType}/${refId}`);
      setPhotos(data);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refType, refId]);

  const doUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const form = new FormData();
    for (const f of fileList) form.append("files", f);
    if (note) form.append("note", note);
    try {
      const res = await fetch(`${API_BASE}/photos/${refType}/${refId}`, {
        method: "POST", body: form,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      toast.success(`${data.uploaded} Foto${data.uploaded > 1 ? "s" : ""} hochgeladen`);
      setNote("");
      load();
    } catch (err) {
      toast.error("Upload fehlgeschlagen: " + err.message);
    } finally { setUploading(false); }
  };

  const removePhoto = async (photoId) => {
    if (!window.confirm("Foto wirklich löschen?")) return;
    try {
      await api.delete(`/photos/${photoId}`);
      load();
    } catch (err) { toast.error("Löschen fehlgeschlagen"); }
  };

  if (!refId) return null;

  return (
    <div className="mt-3" data-testid={`photos-${refType}-${refId}`}>
      {label && <div className="overline mb-2">{label}</div>}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {!compact && (
          <Input placeholder="Optionale Notiz (z.B. 'Karton beschädigt')"
                 value={note} onChange={(e) => setNote(e.target.value)}
                 data-testid={`photo-note-${refType}`}
                 className="rounded-full flex-1 min-w-[200px] max-w-md" />
        )}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment"
               hidden onChange={(e) => doUpload(e.target.files)} />
        <input ref={inputRef} type="file" accept="image/*" multiple
               hidden onChange={(e) => doUpload(e.target.files)} />
        <Button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading || atMax}
                data-testid={`photo-camera-${refType}`}
                className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
          <Camera size={14} className="mr-1" /> Kamera
        </Button>
        <Button type="button" onClick={() => inputRef.current?.click()} disabled={uploading || atMax}
                data-testid={`photo-upload-${refType}`}
                variant="outline" className="rounded-full">
          <Upload size={14} className="mr-1" /> Datei
        </Button>
        {atMax && <span className="text-xs text-[var(--text-muted)]">Max. {maxPhotos} Fotos erreicht</span>}
      </div>

      {photos.length === 0 ? (
        <div className="text-xs text-[var(--text-muted)] italic flex items-center gap-1">
          <ImageIcon size={12} /> Noch keine Fotos hinterlegt.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {photos.map((p) => (
            <div key={p.photo_id} className="relative group rounded-xl overflow-hidden border border-[var(--border)] bg-[var(--surface-alt)]"
                 data-testid={`photo-tile-${p.photo_id}`}>
              <AuthedImage photoId={p.photo_id} alt={p.filename}
                           className="w-full aspect-square object-cover" />
              <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/80 to-transparent text-white text-[10px]">
                <div className="font-mono">{new Date(p.uploaded_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</div>
                <div className="opacity-80 truncate">{p.uploaded_by_name || "—"}</div>
                {p.note && <div className="opacity-80 truncate italic">„{p.note}"</div>}
              </div>
              <button onClick={() => removePhoto(p.photo_id)}
                      data-testid={`btn-delete-photo-${p.photo_id}`}
                      title="Foto löschen"
                      className="absolute top-1 right-1 h-7 w-7 rounded-full bg-red-600/90 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
